import React, { useEffect, useState, useCallback } from "react";
import { Eye, Globe, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { BlockSkeleton } from "@/components/Loading";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function deviceFromUA(ua = "") {
  if (!ua) return "Unknown";
  const s = ua.toLowerCase();
  let os = "";
  if (s.includes("iphone") || s.includes("ipad") || s.includes("ios")) os = "iOS";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("mac os")) os = "Mac";
  else if (s.includes("windows")) os = "Windows";
  else if (s.includes("linux")) os = "Linux";
  let br = "";
  if (s.includes("edg/")) br = "Edge";
  else if (s.includes("chrome/") && !s.includes("edg/")) br = "Chrome";
  else if (s.includes("firefox/")) br = "Firefox";
  else if (s.includes("safari/") && !s.includes("chrome/")) br = "Safari";
  return [br, os].filter(Boolean).join(" · ") || "Other";
}

function isNewToday(iso) {
  if (!iso) return false;
  return String(iso).slice(0, 10) === new Date().toISOString().slice(0, 10);
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card data-testid={testId} className="flex items-center gap-4 rounded-[14px] border-[var(--pb-border)] bg-white p-5 shadow-[var(--pb-shadow-sm)]">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(24,52,74,0.06)]">
        <Icon className="h-5 w-5 text-[#526E7E]" />
      </div>
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-[rgba(48,56,60,0.55)]">{label}</div>
        <div className="font-display text-2xl font-semibold text-[#18344A]">{value}</div>
      </div>
    </Card>
  );
}

export default function Visitors() {
  const [data, setData] = useState({ visitors: [], total_unique: 0, new_today: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/visitors")
      .then((res) => setData(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const { visitors, total_unique, new_today } = data;

  return (
    <div className="space-y-6">
      <PageHeader title="Site Visitors" subtitle="Every unique visitor to the public website, newest first.">
        <Button data-testid="visitors-refresh" onClick={load} variant="outline" className="h-9 gap-2 rounded-full">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </PageHeader>

      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard icon={Globe} label="Total unique visitors" value={fmtNumber(total_unique)} testId="stat-total-unique" />
        <StatCard icon={Sparkles} label="New today" value={fmtNumber(new_today)} testId="stat-new-today" />
      </div>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        {loading ? (
          <div className="p-4"><BlockSkeleton className="h-80" /></div>
        ) : visitors.length === 0 ? (
          <EmptyState title="No visitors yet" message="As soon as someone opens the public site, they'll show up here." icon={Eye} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="visitors-table">
              <TableHeader>
                <TableRow className="bg-[rgba(24,52,74,0.04)]">
                  <TableHead>IP Address</TableHead>
                  <TableHead>First seen</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead>Landing page</TableHead>
                  <TableHead>Device</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visitors.map((v) => (
                  <TableRow key={v.id || v.ip} data-testid={`visitor-row-${v.ip}`} className="hover:bg-[rgba(183,165,142,0.1)]">
                    <TableCell className="font-medium text-[#30383C]">
                      <span className="inline-flex items-center gap-2">
                        {v.ip}
                        {isNewToday(v.first_seen) && (
                          <Badge className="bg-[rgba(82,110,126,0.14)] text-[10px] font-semibold uppercase tracking-wide text-[#526E7E] hover:bg-[rgba(82,110,126,0.14)]">New</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">{fmtDateTime(v.first_seen)}</TableCell>
                    <TableCell className="text-sm text-[rgba(48,56,60,0.7)]">{fmtDateTime(v.last_seen)}</TableCell>
                    <TableCell className="text-right text-sm">{fmtNumber(v.visits)}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-[rgba(48,56,60,0.7)]">{v.first_path || "\u2014"}</TableCell>
                    <TableCell className="text-sm text-[rgba(48,56,60,0.7)]">{deviceFromUA(v.user_agent)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
